import React, {useState, useEffect, useMemo, useCallback} from 'react';
import {AccordionDetails, Box, Typography, Divider} from '@mui/material';
import ArrTooltipWrapper from '../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ArrToggle} from '../../../../common/arr-toggle/ArrToggle';
import {ReactComponent as InfoIcon} from '../../../../../assets/images/info-icon.svg';
import ArrRadio from '../../../../../components/common/arr-radio/ArrRadio';
import {useTranslation, Trans} from 'react-i18next';
import FileTypeSelector from '../../../../common/arr-fileTypeSelector/FileTypeSelector';
import './NativeComponent.scss';
import ArrFilterChip from '../../../../common/arr-filter-chip/ArrFilterChip';
import TagsTreePanel from '../../../create-new-production/common/tags-tree-panel/TagsTreePanel';
import {ExtendedTreeItemProps} from '../../../../../shared/interfaces';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {
  FileTypeItem,
  MetaDataField,
  TagItem,
  TreeNode,
} from '../../../../../api/types/production.type';
import {
  ModuleType,
  FormMode,
  getInsertFieldTypes,
  SlipSheetTreeId,
} from '../../ReProductionUtils';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import ArrButton from '../../../../common/arr-button/ArrButton';
import {useGetNativeComponentReproduction} from '../../../../../hooks/productions/useReProductions';
import {transformTagTreeData} from '../../../create-new-production/CreateNewProductionHelpers';
import {NativeRedactionsSection} from '../../../create-new-production/production-components/native-components/NativeRedactionsSection';
import {
  buildSpecifiedRedactionList,
  createDefaultNativeRedactionSettings,
  hydrateNativeRedactionSettings,
  mapMetadataToSelectItems,
  mapNativeRedactionStyleOptions,
  NativeRedactionSettingsState,
  serializeNativeRedactionSettings,
} from '../../../create-new-production/production-components/native-components/nativeRedactionUtils';

const READONLY_NATIVE_REPRODUCTION_TYPES = new Set([
  'ADD_REMOVE_REDACTIONS',
  'ADD_REMOVE_BRANDING',
]);

interface NativeComponentProps {
  fileGroupList?: FileTypeItem[];
  tagTreeData?: TreeViewBaseItem<ExtendedTreeItemProps>[];
  redactionTagList?: TreeNode[];
  nativeRedactionStyleList?: {value: string; text: string}[];
  commonMetaDataList?: MetaDataField[];
  commonProductionFieldList?: MetaDataField[];
  productionId: number;
  isProductionSet: boolean;
  onFileGroupsAndTagsChange?: (fileGroups: string, tags: string) => void;
  mode?: FormMode;
  isTemplate?: boolean;
  templateId?: number;
  disableGenerateLST?: boolean;
  isReProduction?: boolean;
  reproductionType?: string;
  editableNativeSubSections?: string[];
}

export const NativeComponent: React.FC<NativeComponentProps> = ({
  fileGroupList = [],
  tagTreeData = [],
  redactionTagList = [],
  nativeRedactionStyleList = [],
  commonMetaDataList = [],
  commonProductionFieldList = [],
  productionId,
  onFileGroupsAndTagsChange,
  mode = FormMode.CREATE,
  isTemplate = false,
  disableGenerateLST = false,
  isReProduction = false,
  reproductionType,
  editableNativeSubSections,
}) => {
  const {t} = useTranslation();
  const isViewMode = mode === FormMode.VIEW;

  const lockedClass = useCallback(
    (subSection: string): string => {
      if (isViewMode) return '';
      if (!editableNativeSubSections?.length) return '';
      return editableNativeSubSections.includes(subSection)
        ? ''
        : ' section--locked';
    },
    [editableNativeSubSections, isViewMode]
  );

  const isSubSectionEditable = useCallback(
    (subSection: string) => {
      if (isViewMode) return false;
      if (!editableNativeSubSections?.length) return true;
      return editableNativeSubSections.includes(subSection);
    },
    [editableNativeSubSections, isViewMode]
  );

  const isFileSettingsDisabled = !isSubSectionEditable('fileSettings');
  const isRedactionsDisabled = !isSubSectionEditable('redactions');
  const isGenerateLstDisabled =
    !isSubSectionEditable('generateLst') || disableGenerateLST;

  const options = useMemo(
    () => [
      {
        label: t(
          'productionsAndExports.createNewProduction.productionComponents.native.parentsOfPrivilegedAndRedacted'
        ),
        value: '0',
      },
      {
        label: t(
          'productionsAndExports.createNewProduction.productionComponents.native.completeFamiliesOfPrivilegedAndRedacted'
        ),
        value: '1',
      },
    ],
    [t]
  );

  const selectedSecurityGroupId = useSelector(
    (state: RootState) => state.production.securityGroupId
  );

  const {
    data: nativeComponentDataFromProduction,
    isLoading: isNativeComponentLoadingFromProduction,
    error: nativeComponentErrorFromProduction,
  } = useGetNativeComponentReproduction(
    {
      SecurityGroupId: selectedSecurityGroupId,
      moduletype: ModuleType.REPRODUCTION,
      productionId: productionId,
      exportProductionId: 0,
    },
    !!selectedSecurityGroupId && !isTemplate && productionId > 0
  );

  const nativeComponentData = nativeComponentDataFromProduction;
  const isNativeComponentLoading = isNativeComponentLoadingFromProduction;
  const nativeComponentError = nativeComponentErrorFromProduction;

  const redactionTagTreeData = useMemo(
    () =>
      redactionTagList.length > 0
        ? transformTagTreeData(
            redactionTagList,
            SlipSheetTreeId.REDACTION_TAG_TREE
          )
        : [],
    [redactionTagList]
  );

  const redactionStyleOptions = useMemo(
    () => mapNativeRedactionStyleOptions(nativeRedactionStyleList),
    [nativeRedactionStyleList]
  );

  const insertFieldTypes = useMemo(() => getInsertFieldTypes(), []);
  const metadataFieldOptions = useMemo(
    () => mapMetadataToSelectItems(commonMetaDataList),
    [commonMetaDataList]
  );
  const productionFieldOptions = useMemo(
    () => mapMetadataToSelectItems(commonProductionFieldList),
    [commonProductionFieldList]
  );

  const [generateNative, setGenerateNative] = useState(false);
  const [exportNative, setExportNative] = useState(false);
  const [selected, setSelected] = useState('');

  const [isTagsFlyoutOpen, setIsTagsFlyoutOpen] = useState(false);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedObjects, setSelectedObjects] = useState<
    ExtendedTreeItemProps[]
  >([]);
  const [selectedFileTypeIds, setSelectedFileTypeIds] = useState<string[]>([]);
  const [, setComponentIsDirty] = useState<boolean>(false);
  const [previousSelectedIds, setPreviousSelectedIds] =
    useState<string[]>(selectedIds);
  const [redactionSettings, setRedactionSettings] =
    useState<NativeRedactionSettingsState>(
      createDefaultNativeRedactionSettings
    );
  const [redactionGlobalSelectedTags, setRedactionGlobalSelectedTags] =
    useState<TagItem[]>([]);
  const [isRedactionWithoutTags, setIsRedactionWithoutTags] =
    useState<boolean>(true);

  const getNativeInfoIconA11yProps = useCallback(
    (label: string) => ({
      'aria-label': label,
      tabIndex: 0,
    }),
    []
  );

  const nativeViewModeDisabledA11yProps = isViewMode
    ? {tabIndex: -1 as const}
    : {};

  const getCurrentConfig = () => {
    const selectedFileTypeIdsString = selectedFileTypeIds.join(',');
    const selectedTagIds = selectedObjects.map((obj) => obj.id).join(',');

    return {
      isSelectAllFileTypes: false,
      produceLoadFile: generateNative,
      generateHTMLforEmails: false,
      produceNativeForTechDocs: exportNative,
      producedLockedFiles: false,
      protectionOnExcelFile: false,
      nativeComponentmodel: '',
      nativeSelectedFileGroups: selectedFileTypeIdsString
        ? `,${selectedFileTypeIdsString}`
        : '',
      nativeSelectedTags: selectedTagIds,
      eRedactedAndPrevilededFamily:
        selected === '0' ? 0 : selected === '1' ? 1 : 0,
      redactionSettings: serializeNativeRedactionSettings(
        redactionSettings,
        redactionGlobalSelectedTags,
        isRedactionWithoutTags,
        nativeRedactionStyleList
      ),
    };
  };

  (window as any).getNativeConfigReProduction = getCurrentConfig;

  const isNativeReadOnly = READONLY_NATIVE_REPRODUCTION_TYPES.has(
    reproductionType ?? ''
  );

  const flattenTree = useCallback(
    (
      nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
    ): ExtendedTreeItemProps[] =>
      nodes.reduce<ExtendedTreeItemProps[]>((acc, node) => {
        acc.push(node);
        if (node.children) acc.push(...flattenTree(node.children));
        return acc;
      }, []),
    []
  );

  useEffect(() => {
    if (!nativeComponentData) return;

    const fileGroups = nativeComponentData.nativeSelectedFileGroups ?? '';
    const tags = nativeComponentData.nativeSelectedTags ?? '';

    const tagIds = tags
      ? tags
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean)
      : [];
    setSelectedIds(tagIds);

    if (nativeComponentData.eRedactedAndPrevilededFamily !== undefined) {
      setSelected(String(nativeComponentData.eRedactedAndPrevilededFamily));
    }

    setGenerateNative(nativeComponentData.produceLoadFile ?? false);
    setExportNative(nativeComponentData.produceNativeForTechDocs ?? false);

    const hydratedRedactions = hydrateNativeRedactionSettings(
      nativeComponentData.redactionSettings,
      redactionTagTreeData
    );
    setRedactionSettings(hydratedRedactions.redactionSettings);
    setRedactionGlobalSelectedTags(
      hydratedRedactions.redactionGlobalSelectedTags
    );
    setIsRedactionWithoutTags(hydratedRedactions.isRedactionWithoutTags);

    if (isNativeReadOnly) {
      if (fileGroups) {
        setSelectedFileTypeIds(
          fileGroups
            .split(',')
            .map((id) => id.trim())
            .filter(Boolean)
        );
      }
    }

    onFileGroupsAndTagsChange?.(fileGroups, tags);
  }, [
    nativeComponentData,
    isNativeReadOnly,
    onFileGroupsAndTagsChange,
    redactionTagTreeData,
  ]);

  useEffect(() => {
    const specifiedRedactionList = buildSpecifiedRedactionList(
      redactionGlobalSelectedTags,
      isRedactionWithoutTags
    );

    setRedactionSettings((prev) => ({
      ...prev,
      specifiedRedactionList,
    }));
  }, [redactionGlobalSelectedTags, isRedactionWithoutTags]);

  useEffect(() => {
    if (!tagTreeData?.length || !selectedIds?.length) return;

    const selectedObjs = flattenTree(tagTreeData).filter((node) =>
      selectedIds.includes(String(node.id))
    );

    if (selectedObjs.length > 0) {
      setSelectedObjects(selectedObjs);
    }
  }, [tagTreeData, selectedIds, flattenTree]);

  useEffect(() => {
    if (fileGroupList && fileGroupList.length > 0) {
      const preSelected = fileGroupList
        .filter((item) => item.isFileTypeSelected)
        .map((item) => item.fileTypeID.toString());
      setSelectedFileTypeIds(preSelected);
    }
  }, [fileGroupList]);

  useEffect(() => {
    if (onFileGroupsAndTagsChange) {
      const selectedFileTypeIdsString = selectedFileTypeIds.join(',');
      const fileGroups = selectedFileTypeIdsString
        ? `,${selectedFileTypeIdsString}`
        : '';
      const tags = selectedObjects.map((obj) => obj.id).join(',');
      onFileGroupsAndTagsChange(fileGroups, tags);
    }
  }, [selectedFileTypeIds, selectedObjects, onFileGroupsAndTagsChange]);

  const handleToggleChange = (
    _e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    if (isGenerateLstDisabled) return;
    setComponentIsDirty(true);
    setGenerateNative(checked);
  };

  const handleExportChange = (
    _e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    if (isFileSettingsDisabled) return;
    setComponentIsDirty(true);
    setExportNative(checked);
  };

  const handleTags = (data: TreeViewBaseItem<ExtendedTreeItemProps>[]) => {
    if (isFileSettingsDisabled) return;
    const nextSelectedIds = data.map((item) => item.id);
    setSelectedIds(nextSelectedIds);

    const flatTree = flattenTree(tagTreeData);
    const nextSelectedObjects = flatTree.filter((node) =>
      nextSelectedIds.includes(node.id)
    );

    setSelectedObjects(nextSelectedObjects);
  };

  const handleRemove = (value: string, type: 'id' | 'label' = 'id') => {
    if (isFileSettingsDisabled) return;
    const updated = selectedObjects.filter((item) =>
      type === 'id' ? item.id !== value : item.label !== value
    );
    setSelectedObjects(updated);
    setSelectedIds(updated.map((obj) => obj.id));
  };

  const handleFileTypeSelectionChange = useCallback(
    (nextSelectedIds: string[]) => {
      if (isFileSettingsDisabled) return;

      if (
        JSON.stringify(nextSelectedIds) !== JSON.stringify(previousSelectedIds)
      ) {
        setSelectedFileTypeIds(nextSelectedIds);
        setComponentIsDirty(true);
        setPreviousSelectedIds(nextSelectedIds);
      } else {
        setComponentIsDirty(false);
      }
    },
    [isFileSettingsDisabled, previousSelectedIds]
  );

  const handleRedactionSettingsChange = (
    nextSettings: NativeRedactionSettingsState
  ) => {
    if (isRedactionsDisabled) return;
    setRedactionSettings(nextSettings);
    setComponentIsDirty(true);
  };

  const availableFileTypes = useMemo(() => {
    const baseFileTypes = fileGroupList;

    if (nativeComponentData?.nativeSelectedFileGroups) {
      const ids = nativeComponentData.nativeSelectedFileGroups
        .split(',')
        .map((id) => id.trim());
      return baseFileTypes.map((fileType) => ({
        ...fileType,
        isFileTypeSelected: ids.includes(fileType.fileTypeID.toString()),
      }));
    }

    return baseFileTypes;
  }, [nativeComponentData, fileGroupList]);

  if (isNativeComponentLoading) {
    return (
      <Box className="native-component">
        <Typography>Loading native component data...</Typography>
      </Box>
    );
  }

  if (nativeComponentError) {
    return (
      <Box className="native-component">
        <Typography color="error">
          Error loading native component data: {nativeComponentError.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      className={`native-component native-accordion${isReProduction ? ' re-production' : ''}${isViewMode ? ' native-component--view-mode' : ''}`}
      data-test-id={'NativeAccordionReproduction'}
    >
      <AccordionDetails className="native-accordion-details">
        <Box className="native-component-content">
          <Box
            className={`native-file-settings-section${lockedClass('fileSettings')}`}
          >
            <Box className="native-intro-section">
              <div className="produce-native-files">
                <div className="produce-header">
                  <Typography variant="subtitle1" className="produce-title">
                    {t(
                      'productionsAndExports.createNewProduction.productionComponent.produceNativeFile'
                    )}
                  </Typography>
                  <ArrTooltipWrapper
                    title="Produce Native Files"
                    trigger={
                      <InfoIcon
                        aria-label="Info"
                        className="view-user-info-icon"
                        tabIndex={0}
                      />
                    }
                  >
                    <Trans
                      i18nKey="productionsAndExports.createNewProduction.productionComponents.native.nativeDocumentSpecificationDescription"
                      components={{br: <br />}}
                    />
                  </ArrTooltipWrapper>
                </div>
                <Box className="produce-description">
                  <Typography className="typo-text body3_size">
                    {t(
                      'productionsAndExports.createNewProduction.productionComponent.produceNativeDescription'
                    )}
                  </Typography>

                  <Typography className="typo-text body3_size">
                    {t(
                      'productionsAndExports.createNewProduction.productionComponent.produceNativeDescription1'
                    )}
                  </Typography>
                </Box>
              </div>
            </Box>

            <Divider className="native-divider" />

            <Box className="native-file-types-section">
              <div className="tag-files">
                <Typography
                  variant="subtitle1"
                  className="heading typo-text subhead4_size"
                >
                  {t(
                    'productionsAndExports.createNewProduction.productionComponents.native.selectFileTags'
                  )}
                </Typography>
              </div>
              <Box className="native-filetag-container">
                <Box className="native-fileType">
                  <FileTypeSelector
                    fileGroupList={availableFileTypes}
                    onSelectionChange={handleFileTypeSelectionChange}
                    disabled={isFileSettingsDisabled}
                    checkboxDataTestId="native-component-file-type-selector-reproduction"
                  />
                </Box>

                <Box className="native-fileType">
                  <div className="filetype-selector">
                    <div className="filetype-header">
                      <Typography className="typo-text subhead5_size filetype-title">
                        {t(
                          'productionsAndExports.createNewProduction.productionComponent.tags'
                        )}
                      </Typography>
                      <ArrButton
                        variant="primary"
                        size="small"
                        dataTestId="native-component-add-tags-button-reproduction"
                        onClick={() =>
                          !isFileSettingsDisabled && setIsTagsFlyoutOpen(true)
                        }
                        disabled={isFileSettingsDisabled}
                      >
                        {t(
                          'productionsAndExports.createNewProduction.productionComponent.addTags'
                        )}
                      </ArrButton>
                    </div>

                    <div className="selected-types">
                      {selectedObjects.map((item) => (
                        <ArrFilterChip
                          key={item.id}
                          classNames="chip"
                          filterName=""
                          selections={[{id: item.id, value: item.label}]}
                          variant="v1"
                          onRemove={() => handleRemove(item.id)}
                          disabled={isFileSettingsDisabled}
                          dataTestId={''}
                        />
                      ))}
                    </div>
                  </div>
                  {isTagsFlyoutOpen && (
                    <TagsTreePanel
                      title={t(
                        'productionsAndExports.createNewProduction.datComponent.selectTags'
                      )}
                      tooltip={{
                        title: t(
                          'productionsAndExports.createNewProduction.productionComponents.native.tooltips.selectTags.title'
                        ),
                        content: t(
                          'productionsAndExports.createNewProduction.productionComponents.native.tooltips.selectTags.content'
                        ),
                      }}
                      type="flyout"
                      tags={tagTreeData}
                      selectedTags={selectedObjects}
                      isOpen={isTagsFlyoutOpen}
                      onClose={() => setIsTagsFlyoutOpen(false)}
                      setSelectedTags={(items) => {
                        handleTags(items);
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Box>

            <Divider className="native-divider" />

            <Box className="native-withhold-section">
              <Box className="native-section">
                <Typography
                  variant="subtitle1"
                  className="typo-text subhead4_size section-title"
                >
                  {t(
                    'productionsAndExports.createNewProduction.productionComponents.native.dontProducedNative'
                  )}
                </Typography>
                <ArrTooltipWrapper
                  title="Privileged and Redacted Document Family Members"
                  trigger={
                    <InfoIcon
                      className="view-user-info-icon"
                      tabIndex={0}
                      aria-label="Privileged and Redacted Document Family Members info"
                    />
                  }
                >
                  <Trans
                    i18nKey="productionsAndExports.createNewProduction.productionComponents.native.dontProducedNativeDescription"
                    components={{
                      br: <br />,
                      strong: <strong />,
                      p: <p />,
                    }}
                  />
                </ArrTooltipWrapper>
              </Box>

              <Box className="native-withhold-inputs">
                <Box className="native-radio-group">
                  <ArrRadio
                    dataTestId="controlled-radio-buttons-group-reproduction"
                    size="small"
                    name="controlled-radio-buttons-group"
                    onChange={(value) => {
                      if (isFileSettingsDisabled) return;
                      setComponentIsDirty(true);
                      setSelected(value);
                    }}
                    value={selected}
                    data={options}
                    className="section-title custom-label"
                    disabled={isFileSettingsDisabled}
                  />
                </Box>

                <Box className="produce-description native-note">
                  <Typography className="typo-text body3_size">
                    {t(
                      'productionsAndExports.createNewProduction.productionComponent.note'
                    )}
                    :{' '}
                    {t(
                      'productionsAndExports.createNewProduction.productionComponent.noteContent'
                    )}
                  </Typography>
                </Box>

                <Box className="native-toggle">
                  <ArrToggle
                    disabled={isFileSettingsDisabled}
                    checked={exportNative}
                    onChange={handleExportChange}
                    dataTestId="chkNoNativeForTechIssueTagReproduction"
                  />
                  <Typography className="toggle-text typo-text subhead5_size">
                    {t(
                      'productionsAndExports.createNewProduction.productionComponents.native.dontExportNative'
                    )}
                  </Typography>
                  <ArrTooltipWrapper
                    title="Enable Placeholders by Selecting File Types"
                    trigger={
                      <InfoIcon className="view-user-info-icon" tabIndex={0} />
                    }
                  >
                    <Trans
                      i18nKey="productionsAndExports.createNewProduction.productionComponents.native.nativePlaceholdersByFileTypeDescription"
                      components={{
                        br: <br />,
                        strong: <strong />,
                        p: <p />,
                      }}
                    />
                  </ArrTooltipWrapper>
                </Box>
              </Box>
            </Box>
          </Box>
          {/* remove native-redactions-visual-hide once rnfv feature live */}
          <Box
            className={`native-redactions-section native-redactions-visual-hide${lockedClass('redactions')}`}
          >
            <NativeRedactionsSection
              settings={redactionSettings}
              redactionGlobalSelectedTags={redactionGlobalSelectedTags}
              isRedactionWithoutTags={isRedactionWithoutTags}
              redactionTagTreeData={redactionTagTreeData}
              redactionStyleOptions={redactionStyleOptions}
              insertFieldTypes={insertFieldTypes}
              metadataFieldOptions={metadataFieldOptions}
              productionFieldOptions={productionFieldOptions}
              disabled={isRedactionsDisabled}
              onSettingsChange={handleRedactionSettingsChange}
              onGlobalTagsChange={(tags) => {
                if (isRedactionsDisabled) return;
                setRedactionGlobalSelectedTags(tags);
                setComponentIsDirty(true);
              }}
              onRedactionWithoutTagsChange={(checked) => {
                if (isRedactionsDisabled) return;
                setIsRedactionWithoutTags(checked);
                setComponentIsDirty(true);
              }}
              onDirty={() => setComponentIsDirty(true)}
              getInfoIconA11yProps={getNativeInfoIconA11yProps}
              viewModeDisabledA11yProps={nativeViewModeDisabledA11yProps}
            />
          </Box>

          <Divider className="native-divider" />

          <Box
            className={`native-generate-lst-section native-toggle native-lst-toggle${lockedClass('generateLst')}`}
          >
            <ArrToggle
              disabled={isGenerateLstDisabled}
              checked={generateNative}
              onChange={handleToggleChange}
              dataTestId="native-toggle-reproduction"
            />
            <Typography className="toggle-text typo-text subhead4_size">
              {t(
                'productionsAndExports.createNewProduction.productionComponents.native.generateNative'
              )}
            </Typography>
            <ArrTooltipWrapper
              title="Generate Load File"
              trigger={
                <InfoIcon className="view-user-info-icon" tabIndex={0} />
              }
            >
              <Trans
                i18nKey="productionsAndExports.createNewProduction.productionComponents.native.generateNativeDescription"
                components={{
                  br: <br />,
                  strong: <strong />,
                  p: <p />,
                }}
              />
            </ArrTooltipWrapper>
          </Box>
        </Box>
      </AccordionDetails>
    </Box>
  );
};
